import { useState, useEffect, useCallback } from 'react';
import { getAccessTokenSync } from './useAccessToken';

const SB_URL = process.env.REACT_APP_SUPABASE_URL;
const SB_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

async function sbFetch(table, params = '') {
  const token = getAccessTokenSync();
  const headers = {
    'apikey': SB_KEY,
    'Authorization': `Bearer ${token || SB_KEY}`,
    'Content-Type': 'application/json',
  };
  const res = await fetch(`${SB_URL}/rest/v1/${table}?${params}`, { headers });
  if (!res.ok) throw new Error(`${table}: ${res.status} ${res.statusText}`);
  return res.json();
}

/**
 * Fetches critical financial data from Supabase via direct REST calls.
 * Bypasses supabase-js client to avoid navigator.locks issues.
 * Pass the authenticated user so it re-fetches after login.
 */
export default function useSupabaseData(user, companyId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async (cid) => {
    if (!SB_URL || !SB_KEY) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Filter by company_id if provided
    const cFilter = cid ? `company_id=eq.${cid}&` : '';

    try {
      const [gics, ap, recv, cap, metricsRaw] = await Promise.all([
        sbFetch('gic_instruments', `${cFilter}order=maturity_date.asc.nullsfirst`),
        sbFetch('ap_aging', `${cFilter}order=amount.desc`),
        sbFetch('receivables', cFilter.slice(0, -1)),
        sbFetch('capital_raises', `${cFilter}order=id.asc`),
        sbFetch('company_metrics', cFilter.slice(0, -1)),
      ]);

      const metrics = {};
      (metricsRaw || []).forEach(row => {
        metrics[row.metric_key] = row.metric_value;
      });

      setData({ gics, apAging: ap, receivables: recv, capitalRaises: cap, metrics });
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  }, []);

  // Fetch when user or company changes
  const userId = user?.id || null;
  useEffect(() => {
    if (userId && companyId) fetchAll(companyId);
  }, [userId, companyId, fetchAll]);

  return { data, loading, error, refetch: fetchAll };
}
